import { isPlatformBrowser } from '@angular/common';
import { inject, PLATFORM_ID } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';

import { AUTH_ROUTE_PATHS } from '../../constants/auth-routes.constants';
import { environment } from '../../../environments/environment';
import { TOKEN_LOGIN_PATH } from '../services/session-expiry.service';
import { hasLegacyAppBase, LEGACY_PRODUCT_MAPPING_PATH, legacyAppUrl } from '../utils/legacy-app-url';

/**
 * sessionStorage flag set by `AuthRedirectComponent` (SSO flow) and
 * `SecurityQuestionsComponent` (email/password flow) once the user has been verified
 * this session. It is the app's "authenticated" signal. Both flows write it BEFORE
 * navigating into a guarded route, so gating on it cannot loop a successful login.
 */
const VERIFIED_FLAG = 'isVerified';

/**
 * Route guard for every protected route: blocks a page until the user has
 * authenticated this session, and sends an unauthenticated visitor back to the
 * sign-in entry point for the current environment.
 *
 * - **Production** → a full `window.location` bounce to the legacy app's Product
 *   Mapping page, the same SSO entry point `AuthRedirectComponent` and the Home click
 *   already use. A visitor whose legacy session is alive is handed straight back into
 *   the app with a fresh token; one whose session is gone gets the legacy login.
 * - **Production with no usable `OLD_NEW_URL`** → the in-app email/password sign-in flow.
 *   `legacyAppUrl()` deliberately degrades a blank/relative base to a SAME-ORIGIN path
 *   (its callers only open links), but a guard cannot bounce out of the SPA to a path
 *   that lands back INSIDE it: the `**` route absorbs `/ProductMapping/MappingStructure`
 *   to `/dashboard`, the guard fires again, and the browser hard-reloads forever. So the
 *   external bounce is taken only when {@link hasLegacyAppBase} confirms an absolute
 *   http(s) base; otherwise the visitor gets a sign-in page that actually works.
 * - **Development** → an in-app redirect to {@link TOKEN_LOGIN_PATH}.
 *
 * WHY it is no longer production-exempt (it was `devTokenAuthGuard`, returning `true`
 * whenever `environment.production`): that left EVERY route unprotected in the
 * deployed app. Pasting `/dashboard` after a logout — or in a fresh tab, since the
 * session lives in per-tab sessionStorage — rendered the full page chrome with empty
 * data and no way back to a login, because each API call failed for want of a token.
 * "Renders but with nothing in it" is the visible symptom of a missing auth gate, not
 * a data bug.
 *
 * SSR/prerender safe: the server cannot read sessionStorage, so it allows the render
 * and the client re-evaluates on hydration. This is why an unauthenticated hard load
 * still paints the prerendered shell for an instant before the bounce.
 */
export const authGuard: CanActivateFn = () => {
  const router = inject(Router);
  const platformId = inject(PLATFORM_ID);

  if (!isPlatformBrowser(platformId)) {
    return true;
  }

  if (sessionStorage.getItem(VERIFIED_FLAG) === 'true') {
    return true;
  }

  if (environment.production) {
    if (hasLegacyAppBase()) {
      // Leave the SPA entirely — the sign-in flow lives in the legacy app in production.
      window.location.href = legacyAppUrl(LEGACY_PRODUCT_MAPPING_PATH);
      return false;
    }
    // Misconfigured deploy: bouncing to a same-origin path would hard-reload in a loop.
    return router.parseUrl(AUTH_ROUTE_PATHS.emailVerification);
  }

  return router.parseUrl(TOKEN_LOGIN_PATH);
};
